import React, { useState, useEffect } from "react";
import { message, Alert } from "antd";
import { DynamicTable } from "dynamic-table-builder";
import dayjs from "dayjs";
import weekday from "dayjs/plugin/weekday";
import weekOfYear from "dayjs/plugin/weekOfYear";
import localeData from "dayjs/plugin/localeData";
import updateLocale from "dayjs/plugin/updateLocale";

// Configure dayjs plugins
dayjs.extend(weekday);
dayjs.extend(weekOfYear);
dayjs.extend(localeData);
dayjs.extend(updateLocale);

// Set locale to ensure weekday functions work properly
dayjs.updateLocale("en", {
  weekStart: 1
});
import Heading from "../../../../components/Heading";
import { ReportsService } from "../../../../services/api/reports";
import { useRequestCancellation } from "../../../../hooks/useRequestCancellation";
import "./style.scss";

const ExcessInScanReport = () => {
  // Global request cancellation hook
  const { createNewRequest, isLatestRequest } = useRequestCancellation();
  
  const [authError, setAuthError] = useState(false);
  const [dateRange, setDateRange] = useState(() => {
    const endDate = dayjs();
    const startDate = dayjs().subtract(30, "days");
    return [startDate, endDate];
  });
  const [apiError, setApiError] = useState(null);
  const [tableConfig, setTableConfig] = useState(null);

  // Helper function to compare date ranges
  const areDateRangesEqual = (range1, range2) => {
    if (!range1 || !range2 || !Array.isArray(range1) || !Array.isArray(range2)) {
      return false;
    }
    if (range1.length !== 2 || range2.length !== 2) {
      return false;
    }
    return (
      range1[0].isSame(range2[0], "day") && range1[1].isSame(range2[1], "day")
    );
  };

  // Handle authentication error
  const handleAuthError = async () => {
    setAuthError(true);
    message.error(
      "Authentication failed. Please check your token or login again."
    );
  };

  // This gets called by the DynamicTable whenever filters, pagination or sorting change.
  const handleDataFetch = async (params) => {
    // 🔥 CREATE NEW REQUEST WITH GLOBAL HOOK
    const { signal, requestId } = createNewRequest("excess-inscan-report");
    
    try {
      setApiError(null);

      // Always use the current dateRange state unless explicitly overridden by user filter changes
      // If params.filters.dateRange exists and is different from current state, update the state
      let currentDateRange = dateRange;

      if (
        params?.filters?.dateRange &&
        Array.isArray(params.filters.dateRange)
      ) {
        // Check if the passed date range is different from current state
        const newRange = [
          dayjs(params.filters.dateRange[0]),
          dayjs(params.filters.dateRange[1]),
        ];

        if (!areDateRangesEqual(dateRange, newRange)) {
          // User has changed the date range in the filter, update state
          setDateRange(newRange);
          currentDateRange = newRange;
        }
      }

      // Format the parameters as required by the API - always use currentDateRange
      if (
        !currentDateRange ||
        !Array.isArray(currentDateRange) ||
        currentDateRange.length !== 2
      ) {
        console.error("Invalid date range:", currentDateRange);
        message.error("Invalid date range provided");
        return { data: [], total: 0 };
      }

      const fromDate = currentDateRange[0].format("YYYY-MM-DD");
      const toDate = currentDateRange[1].format("YYYY-MM-DD");

      // Build the query parameters in the format expected by the API
      const requestParams = {
        fromDate,
        toDate,
        pageSize: String(params?.pagination?.pageSize || 10),
        pageNumber: String(params?.pagination?.pageNumber || 1),
      };

      // Add sorting if present
      if (params?.sort?.field) {
        requestParams.sortField = params.sort.field;
        requestParams.sortOrder =
          params.sort.order === "ascend" ? "asc" : "desc";
      }

      // // Add hub filter if selected, otherwise use user's premise ID
      // if (params?.filters?.excessInscanHubId && params.filters.excessInscanHubId.trim()) {
      //   requestParams.excessInscanHubId = params.filters.excessInscanHubId;
      // } else {
      //   // Use user's premise ID as default hubId if no hub is selected
      //   const userData = JSON.parse(localStorage.getItem("userId"));
      //   if (userData?.userInfo?.premiseId) {
      //     requestParams.excessInscanHubId = userData.userInfo.premiseId;
      //   }
      // }

      // Add any additional filters
      if (params?.filters) {
        Object.entries(params.filters).forEach(([key, value]) => {
          if (value && key !== "dateRange") {
            requestParams[key] = String(value);
          }
        });
      }

      const response = await ReportsService.getExcessInscanReport(requestParams, { signal });

      // 🔥 ONLY UPDATE IF THIS IS THE LATEST REQUEST
      if (!isLatestRequest(requestId)) {
        console.log("🔥 Request cancelled - ignoring response");
        return { data: [], total: 0 };
      }

      if (response.status === 403) {
        await handleAuthError();
        return { data: [], total: 0 };
      }

      if (!response?.data) {
        throw new Error("Invalid API response format");
      }

      const result = response.data;

      if (result.status === 200) {
        // Update table configuration with API response metadata
        if (result) {
          const newConfig = {
            ...result,
            pagination: {
              ...result.pagination,
              current: parseInt(requestParams.pageNumber),
              pageSize: parseInt(requestParams.pageSize),
              total: result.pagination?.totalCount || 0,
            },
            // Set default values for filters
            filters: {
              ...result.filters,
              dateRange: {
                ...result.filters?.dateRange,
                defaultValue: areDateRangesEqual(dateRange, currentDateRange)
                  ? dateRange
                  : result.filters?.dateRange?.defaultValue || dateRange,
              },
            },
          };
          setTableConfig(newConfig);
        }

        // Ensure data is properly formatted with unique keys
        const dataWithKeys = Array.isArray(result.data)
          ? result.data.map((item) => ({
            ...item,
            key: item.awbNumber || item.id,
          }))
          : [];

        return {
          data: dataWithKeys,
          total: result.pagination?.totalCount || 0,
          pagination: {
            current: parseInt(requestParams.pageNumber),
            pageSize: parseInt(requestParams.pageSize),
            total: result.pagination?.totalCount || 0,
          },
        };
      } else {
        throw new Error(result.message || "Failed to fetch data");
      }
    } catch (error) {
      // 🔥 DON'T UPDATE STATE IF REQUEST WAS CANCELLED
      if (error.name === "AbortError") {
        console.log("🔥 Request was cancelled at HTTP level - no state update");
        return { data: [], total: 0 };
      }
      
      // Only update if this is still the latest request
      if (!isLatestRequest(requestId)) {
        console.log("🔥 Request cancelled - ignoring error");
        return { data: [], total: 0 };
      }
      
      console.error("Error in handleDataFetch:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error.message ||
        "Error fetching excess inscan data";
      setApiError({
        type: "error",
        title: "Error",
        messages: [errorMessage],
        statusCode: error?.status || 500,
      });
      message.error(`Error fetching data: ${errorMessage}`);
      return { data: [], total: 0 };
    }
  };

  useEffect(() => {
    // Delay the initial data fetch to ensure dayjs is properly configured
    const timer = setTimeout(() => {
      handleDataFetch({
        pagination: { pageNumber: 1, pageSize: 10 },
        filters: { dateRange },
      });
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);

  const renderError = () => {
    if (!apiError && !authError) return null;

    const error = authError ? {
      type: "error",
      title: "Authentication Error",
      messages: [
        "Authentication failed. Please check your token or login again.",
      ],
      statusCode: 403,
    } : apiError;

    return (
      <Alert
        message={error.title}
        description={
          <div>
            {error.statusCode && (
              <div style={{ marginBottom: "8px" }}>
                Status Code: {error.statusCode}
              </div>
            )}
            {error.messages.map((msg, index) => (
              <div
                key={index}
                style={{
                  color: error.type === "error" ? "#ff4d4f" : "#faad14",
                }}
              >
                • {msg}
              </div>
            ))}
          </div>
        }
        type={error.type === "validation" ? "warning" : "error"}
        showIcon
        style={{ marginBottom: "16px" }}
        closable
        onClose={() => {
          if (authError) {
            setAuthError(false);
          } else {
            setApiError(null);
          }
        }}
      />
    );
  };

  return (
    <div className="excess-inscan-report">
      <div style={{ backgroundColor: "white", padding: "8px 16px", borderRadius: "8px" }}>
        <Heading title="Excess Inscan Report" />
      </div>
      <div className="excess-inscan-container">
        {renderError()}
        {tableConfig && (
          <DynamicTable
            config={tableConfig}
            onDataFetch={handleDataFetch}
            defaultFilters={{
              dateRange: {
                defaultValue: dateRange,
              },
            }}
            className="excess-inscan-dynamic-table"
          />
        )}
      </div>
    </div>
  );
};

export default ExcessInScanReport;
